/**
 * MultiSelect — compact checkbox dropdown for the report filters.
 * Empty selection means "All" (the default), matching R1-04.
 */
import { Check, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface Props {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}

export function MultiSelect({ label, options, selected, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const summary =
    selected.length === 0 ? "All" : selected.length === 1 ? selected[0] : `${selected.length} selected`;

  function toggle(opt: string) {
    onChange(selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt]);
  }

  return (
    <div className="relative" ref={ref}>
      <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ink-3)" }}>
        {label}
      </div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full min-w-[150px] inline-flex items-center justify-between gap-2 px-3 py-1.5 rounded-md border hairline text-[13.5px] t-base focus-ring"
        style={{ background: "var(--bg-elev)", color: "var(--ink)" }}
      >
        <span className="truncate max-w-[180px]">{summary}</span>
        <ChevronDown size={14} style={{ color: "var(--ink-3)" }} />
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className="absolute z-20 mt-1 w-[260px] max-h-[320px] overflow-y-auto scroll-quiet rounded-md border hairline shadow-sm py-1"
          style={{ background: "var(--bg-elev)" }}
        >
          <button
            type="button"
            onClick={() => onChange([])}
            className="w-full text-left px-3 py-1.5 text-[12px] font-mono uppercase tracking-[0.1em] t-base hover:bg-[var(--hover)]"
            style={{ color: "var(--ink-3)" }}
          >
            Clear (All)
          </button>
          {options.map((opt) => {
            const on = selected.includes(opt);
            return (
              <button
                type="button"
                role="option"
                aria-selected={on}
                key={opt}
                onClick={() => toggle(opt)}
                className="w-full flex items-center gap-2 text-left px-3 py-1.5 text-[13.5px] t-base hover:bg-[var(--hover)]"
                style={{ color: "var(--ink)" }}
              >
                <span
                  className="inline-flex items-center justify-center w-4 h-4 rounded-sm border hairline shrink-0"
                  style={{ background: on ? "var(--ink)" : "transparent" }}
                >
                  {on && <Check size={11} strokeWidth={3} style={{ color: "var(--bg)" }} />}
                </span>
                <span className="truncate">{opt}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
